import { ParsedUrlQuery } from "querystring";
import { camelCase, omit } from "lodash";
import snakeCase from "lodash/snakeCase";
import Router from "next/router";
import snakecaseKeys from "snakecase-keys";
import { List } from "rivals/shared/interfaces/adminLists";

type GenericRecord = Record<string, string>;

// To remove a filter value, pass in the key with an empty value
export const genShallowPushToURL = (
  path: string,
  replacementParams: { [key: string]: string },
  filters: GenericRecord
): void => {
  const rest = {
    ...omit(Router.query, ["slug"].concat(Object.keys(replacementParams)))
  };
  const currentQuery = { ...rest, ...snakecaseKeys(filters) };
  const dirtyFilters = Object.entries(currentQuery).reduce(
    (accumulator, [key, value]) => {
      return value ? { ...accumulator, [key]: value } : accumulator;
    },
    {}
  );
  const params = new URLSearchParams({ ...snakecaseKeys(dirtyFilters) });
  params.sort();

  let updatedPath = path;
  Object.keys(replacementParams).forEach(subKey => {
    updatedPath = updatedPath.replace(`[${subKey}]`, replacementParams[subKey]);
  });
  Router.push(
    {
      pathname: updatedPath,
      search: params.toString()
    },
    undefined,
    { shallow: true }
  );
};

/**
 * converts an object of {key: value} to appropriate type for genShallowPushToURL
 * acts on value if value is an object or array
 * @param {object} filters - object where key is a string, value can be <number | object | string | string []>
 */
export const objectToQueryString = (filters: object): GenericRecord => {
  let stringsObj = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value && Array.isArray(value)) {
      stringsObj = {
        ...stringsObj,
        [key]: value.length > 0 ? JSON.stringify(value) : ""
      };
    } else if (value && typeof value === "object") {
      stringsObj = {
        ...stringsObj,
        [key]: new URLSearchParams(value).toString()
      };
    } else {
      stringsObj = { ...stringsObj, [key]: value };
    }
  }
  return stringsObj;
};

const isJsonString = (string: unknown): boolean => {
  try {
    JSON.parse(string as string);
  } catch (e) {
    return false;
  }
  return true;
};

/**
 * parse an object of query string values into string, numbers, and string[] with camelCase keys
 * NOTE - Object values will still need to be handled explicitly
 * @param {GenericRecord} GenericRecord - object where key is a string, value can be <string | string []>,
 * typically destructured from router.query
 */
export const queryStringToObject = (query: ParsedUrlQuery): GenericRecord => {
  return Object.entries(query).reduce((acc, [key, value]) => {
    if (isJsonString(value)) {
      acc = { ...acc, [camelCase(key)]: JSON.parse(value as string) };
    } else {
      acc = { ...acc, [camelCase(key)]: value };
    }
    return acc;
  }, {});
};

/**
 * appends query params to a URL
 * @param url - URL to mutate
 * @param params - object of key/value pairs representing the query params' keys and values
 */
export const appendQueryParams = (
  url: string,
  params: { [index: string]: number | string }
): string => {
  const urlObj = new URL(url);
  Object.keys(params).forEach(key =>
    urlObj.searchParams.append(key, params[key].toString())
  );
  return urlObj.toString();
};

/**
 * produces a shareable link to the current page, where the provided list's active version is represented in the URL as
 * a query param with a key of "version"
 * @param list - List to derive shareable link for
 */
export const makeShareableListUrl = (list: List): string => {
  return list.current
    ? window.location.href
    : appendQueryParams(window.location.href, { version: list.version });
};

export const formatStringForUrl = (input: string): string => {
  return snakeCase(input).toLowerCase();
};
